/* eslint-disable import/export */
import moment from 'moment-timezone'
import ID from './base/ID'
import Meter from './Meter'
import { toFloat } from './static/Utils'

export enum ProductionTypes {
  PRODUCTION = 'Production',
  INJECTION = 'Injection'
}

export const productionApolloModel =
`
id
gasVolume
gasEnergy
nglVolume
oilVolume
waterVolume
co2Volume
co2Percent
sulfurVolume
casingPressure
tubingPressure
flowTime
condensateVolume
inputPressureBase
periodStartDate
periodEndDate
periodEndTime
periodTypeId
periodType {
  id
  name
  code
}
postedDate
productionTypeId
productionType {
  id
  name
}
productionRecordStatusId
productionRecordStatus {
  id
  name
  code
}
meterId
operator {
  id
  referenceDescription
}
meter {
  id
  name
  referenceDescriptionAlternative
}
createdUser
modifiedUser
createdDate
modifiedDate
dataInputSource {
  id
  name
}
`

export default interface Production {
    meter?: Meter
}

export interface ProductionParams {
  id: ID | null;
  oilVolume: number | null;
  condensateVolume: number | null;
  gasVolume: number | null;
  gasEnergy: number | null;
  nglVolume: number | null;
  waterVolume: number | null;
  co2Volume: number | null;
  co2Percent: number | null;
  sulfurVolume: number | null;
  tubingPressure: number | null;
  casingPressure: number | null;
  periodStartDate: string | null;
  periodEndDate: string | null;
  meterId: ID | null;
  productionTypeId: ID | null;
  flowTime: number | null;
  productionRecordStatusId: ID | null;
  periodTypeId: ID | null;
  periodEndTime: string | null;
  postedDate: string | null;
  inputPressureBaseId: ID | null;
  meter?: Meter;
}

export interface ProductionForGraphParams {
  gasVolume: number;
  gasEnergy: number;
  oilVolume: number;
  waterVolume: number;
  condensateVolume: number;
  nglVolume: number;
  co2Volume: number;
  sulfurVolume: number;
}

export default class Production {
  private _id: ID | null = null
  private _oilVolume: number | null = null
  private _condensateVolume: number | null = null
  private _gasVolume: number | null = null
  private _gasEnergy: number | null = null
  private _nglVolume: number | null = null
  private _waterVolume: number | null = null
  private _co2Volume: number | null = null
  private _co2Percent: number | null = null
  private _sulfurVolume: number | null = null
  private _tubingPressure: number | null = null
  private _casingPressure: number | null = null
  private _periodStartDate: string | null = null
  private _periodEndDate: string | null = null
  private _periodEndTime: string | null = null
  private _postedDate: string | null = null
  private _periodTypeId: ID | null = null
  private _meterId: ID | null = null
  private _productionTypeId: ID | null = null
  private _flowTime: number | null = null
  private _productionRecordStatusId: ID | null = null
  private _inputPressureBaseId: ID | null = null

  constructor (production?: ProductionParams) {
    if (production !== undefined) {
      if (production.id !== undefined) { this._id = production.id }
      if (production.oilVolume !== undefined) { this._oilVolume = production.oilVolume }
      if (production.condensateVolume !== undefined) { this._condensateVolume = production.condensateVolume }
      if (production.gasVolume !== undefined) { this._gasVolume = production.gasVolume }
      if (production.gasEnergy !== undefined) { this._gasEnergy = production.gasEnergy }
      if (production.nglVolume !== undefined) { this._nglVolume = production.nglVolume }
      if (production.waterVolume !== undefined) { this._waterVolume = production.waterVolume }
      if (production.co2Volume !== undefined) { this._co2Volume = production.co2Volume }
      if (production.co2Percent !== undefined) { this._co2Percent = production.co2Percent }
      if (production.sulfurVolume !== undefined) { this._sulfurVolume = production.sulfurVolume }
      if (production.tubingPressure !== undefined) { this._tubingPressure = production.tubingPressure }
      if (production.casingPressure !== undefined) { this._casingPressure = production.casingPressure }
      if (production.periodStartDate !== undefined) { this._periodStartDate = production.periodStartDate }
      if (production.periodEndDate !== undefined) { this._periodEndDate = production.periodEndDate }
      if (production.meterId !== undefined) { this._meterId = production.meterId }
      if (production.productionTypeId !== undefined) { this._productionTypeId = production.productionTypeId }
      if (production.flowTime !== undefined) { this._flowTime = production.flowTime }
      if (production.productionRecordStatusId !== undefined) { this._productionRecordStatusId = production.productionRecordStatusId }
      if (production.periodEndTime !== undefined) { this._periodEndTime = production.periodEndTime }
      if (production.periodTypeId !== undefined) { this._periodTypeId = production.periodTypeId }
      if (production.postedDate !== undefined) { this._postedDate = production.postedDate }
      if (production.inputPressureBaseId !== undefined) { this._inputPressureBaseId = production.inputPressureBaseId }
    }
  }

  get id (): ID | null {
    return this._id
  }

  set id (value: ID | null) {
    value = this.convertUndefinedToNull(value)
    this._id = value
  }

  get oilVolume (): number | null {
    return this._oilVolume
  }

  set oilVolume (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._oilVolume = value
  }

  get condensateVolume (): number | null {
    return this._condensateVolume
  }

  set condensateVolume (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._condensateVolume = value
  }

  get gasVolume (): number | null {
    return this._gasVolume
  }

  set gasVolume (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._gasVolume = value
  }

  get gasEnergy (): number | null {
    return this._gasEnergy
  }

  set gasEnergy (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._gasEnergy = value
  }

  get nglVolume (): number | null {
    return this._nglVolume
  }

  set nglVolume (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._nglVolume = value
  }

  get waterVolume (): number | null {
    return this._waterVolume
  }

  set waterVolume (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._waterVolume = value
  }

  get co2Volume (): number | null {
    return this._co2Volume
  }

  set co2Volume (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._co2Volume = value
  }

  get co2Percent (): number | null {
    return this._co2Percent
  }

  set co2Percent (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._co2Percent = value
  }

  get sulfurVolume (): number | null {
    return this._sulfurVolume
  }

  set sulfurVolume (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._sulfurVolume = value
  }

  get tubingPressure (): number | null {
    return this._tubingPressure
  }

  set tubingPressure (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._tubingPressure = value
  }

  get casingPressure (): number | null {
    return this._casingPressure
  }

  set casingPressure (value: number | null) {
    value = this.convertUndefinedToNull(value)
    this._casingPressure = value
  }

  get periodStartDate (): string | null {
    return this._periodStartDate
  }

  set periodStartDate (value: string | null) {
    value = this.convertUndefinedToNull(value)
    this._periodStartDate = value
  }

  get periodEndDate (): string | null {
    return this._periodEndDate
  }

  set periodEndDate (value: string | null) {
    value = this.convertUndefinedToNull(value)
    this._periodEndDate = value
  }

  get periodEndTime (): string | null {
    return this._periodEndTime
  }

  set periodEndTime (value: string | null) {
    value = this.convertUndefinedToNull(value)
    this._periodEndTime = value
  }

  get postedDate (): string | null {
    return this._postedDate
  }

  set postedDate (value: string | null) {
    value = this.convertUndefinedToNull(value)
    this._postedDate = value
  }

  get meterId (): ID | null {
    return this._meterId
  }

  set meterId (value: ID | null) {
    value = this.convertUndefinedToNull(value)
    this._meterId = value
  }

  get periodTypeId (): ID | null {
    return this._periodTypeId
  }

  set periodTypeId (value: ID | null) {
    value = this.convertUndefinedToNull(value)
    this._periodTypeId = value
  }

  get productionTypeId (): ID | null {
    return this._productionTypeId
  }

  set productionTypeId (value: ID | null) {
    this._productionTypeId = value
  }

  get productionRecordStatusId (): ID | null {
    return this._productionRecordStatusId
  }

  set productionRecordStatusId (value: ID | null) {
    this._productionRecordStatusId = value
  }

  get flowTime (): number | null {
    return this._flowTime
  }

  set flowTime (value: number | null) {
    this._flowTime = value
  }

  get inputPressureBaseId (): ID | null {
    return this._inputPressureBaseId
  }

  set inputPressureBaseId (value: ID | null) {
    value = this.convertUndefinedToNull(value)
    this._inputPressureBaseId = value
  }

  convertUndefinedToNull (value: any): any {
    if (value === undefined) {
      return null
    } else {
      return value
    }
  }

  addTimezone (): string | null {
    if (this.postedDate !== null) {
      if (/(\+|-)[0-9]{2}:[0-9]{2}/.test(this.postedDate)) {
        return this.postedDate
      } else {
        return moment.utc(this.postedDate, 'MM/DD/YYYY HH:mm')
          .tz(moment.tz.guess(), true)
          .format('MM/DD/YYYY HH:mm Z')
      }
    }
    return this.postedDate
  }

  getApolloFormattedModel (): object {
    return {
      oilVolume: toFloat(this.oilVolume),
      condensateVolume: toFloat(this.condensateVolume),
      gasVolume: toFloat(this.gasVolume),
      gasEnergy: toFloat(this.gasEnergy),
      nglVolume: toFloat(this.nglVolume),
      waterVolume: toFloat(this.waterVolume),
      co2Volume: toFloat(this.co2Volume),
      sulfurVolume: toFloat(this.sulfurVolume),
      tubingPressure: toFloat(this.tubingPressure),
      casingPressure: toFloat(this.casingPressure),
      periodStartDate: this.periodStartDate,
      periodEndDate: this.periodEndDate,
      meterId: Number(this.meterId),
      productionTypeId: this.productionTypeId,
      flowTime: toFloat(this.flowTime),
      productionRecordStatusId: this.productionRecordStatusId,
      inputPressureBaseId: this.inputPressureBaseId,
      periodEndTime: this.periodEndTime,
      periodTypeId: this.periodTypeId,
      postedDate: this.addTimezone()
    }
  }
}
